// Archetype Library and Engine
// Defines archetypal concepts and computes activation scores from network signals

import { NeuralNetworkSimulator } from '../NeuralNetworkSimulator';
import { Node } from '../types';
import { getFractalDimension, getWavePatternSignature } from '../fractalUtils';
import { ArchetypeDefinition, ArchetypeSignalKey } from './archetypeTypes';
import { ALL_ARCHETYPES } from './archetypes';

// Definition moved to archetypeTypes.ts

// Signal key type moved to archetypeTypes.ts

export interface ArchetypeActivation {
	key: string;
	name: string;
	score: number; // 0..1
}

export interface ArchetypeSummary {
	top: ArchetypeActivation[];
	all: ArchetypeActivation[];
}

function clamp01(x: number): number {
	if (Number.isNaN(x)) return 0;
	return x < 0 ? 0 : x > 1 ? 1 : x;
}

// Default library with a compact but useful set of archetypes
export const DEFAULT_ARCHETYPES: ArchetypeDefinition[] = (ALL_ARCHETYPES && ALL_ARCHETYPES.length > 0)
    ? ALL_ARCHETYPES
    : [
        {
            key: 'creator',
            name: 'Creator',
            description:
                'Imagination, language richness, and constructive energy shaping new patterns.',
            category: 'Creativity',
            tags: ['imagination', 'making', 'language'],
            weights: { linguisticComplexity: 0.45, energyLevel: 0.25, stability: 0.15, nodeCount: 0.15 }
        },
        {
            key: 'sage',
            name: 'Sage',
            description: 'Pattern recognition, high stability, and informational density.',
            category: 'Wisdom',
            tags: ['patterns', 'knowledge', 'clarity'],
            weights: { stability: 0.4, edgeCount: 0.25, linguisticComplexity: 0.2, averageNodeEnergy: 0.15 }
        },
        {
            key: 'warrior',
            name: 'Warrior',
            description: 'Momentum, wave intensity, and decisive energy flow.',
            category: 'Power',
            tags: ['action', 'momentum', 'courage'],
            weights: { energyLevel: 0.35, waveIntensity: 0.35, stability: 0.1, nodeCount: 0.2 }
        },
        {
            key: 'healer',
            name: 'Healer',
            description: 'Breath harmony and coherence increasing resilience.',
            category: 'Care',
            tags: ['coherence', 'restoration', 'harmony'],
            weights: { breathingQuality: 0.5, stability: 0.25, energyLevel: 0.25 }
        },
        {
            key: 'trickster',
            name: 'Trickster',
            description: 'Change, exploration, and boundary testing via waves and edges.',
            category: 'Rebellion',
            tags: ['change', 'novelty', 'play'],
            weights: { waveIntensity: 0.4, edgeCount: 0.3, stability: 0.1, linguisticComplexity: 0.2 }
        }
    ];

export class ArchetypeEngine {
	private readonly library: ArchetypeDefinition[];
	private lastSummary: ArchetypeSummary = { top: [], all: [] };
    private externalBoosts: Record<string, number> = {};
    private externalBoostWeight: number = 0.5; // contribution of external boosts (0..1)

	constructor(library: ArchetypeDefinition[] = DEFAULT_ARCHETYPES) {
		this.library = library;
	}

    public setExternalBoosts(boosts: Record<string, number>, weight: number = this.externalBoostWeight): void {
        this.externalBoosts = boosts || {};
        this.externalBoostWeight = Math.max(0, Math.min(1, weight));
    }

	public updateFromNetwork(network: NeuralNetworkSimulator): void {
		const signals = this.extractSignals(network);
		const activations: ArchetypeActivation[] = this.library.map(def => {
			let score = 0;
			for (const [key, weight] of Object.entries(def.weights)) {
				const k = key as ArchetypeSignalKey;
				const s = (signals as any)[k] as number | undefined;
				if (typeof s === 'number' && typeof weight === 'number') {
					score += weight * s;
				}
			}
            const boost = this.externalBoosts[def.key] || 0;
            const combined = clamp01(score + this.externalBoostWeight * clamp01(boost));
            return { key: def.key, name: def.name, score: combined };
		});
		activations.sort((a, b) => b.score - a.score);
		this.lastSummary = { top: activations.slice(0, 3), all: activations };
	}

	public getSummary(): ArchetypeSummary { return this.lastSummary; }

	private extractSignals(network: NeuralNetworkSimulator): Record<ArchetypeSignalKey, number> {
		// Normalize a range of signals to 0..1. These are intentionally conservative
		const nodeCount = network.nodes?.length || 0;
		const edgeCount = network.edges?.length || 0;
		const avgNodeEnergy = nodeCount > 0
			? clamp01((network.nodes.reduce((s: number, n: Node) => s + (n.energy || 0), 0) / nodeCount) / 100)
			: 0;
		const energyLevel = clamp01((network.totalNetworkEnergy || 0) / 20000);
		const linguisticComplexity = clamp01((network.linguisticComplexity || 0) / 10);
		const voicePitch = clamp01(((network.voiceCharacteristics?.pitch as number) || 0) / 800);
		const stability = clamp01((network as any).stabilityMetric || 0);
		const signature = getWavePatternSignature(network);
		const rms = signature.length > 0 ? Math.sqrt(signature.reduce((s, v) => s + v*v, 0) / signature.length) : 0;
		const waveIntensity = clamp01(rms);
		const fractalDimension = clamp01(getFractalDimension(network) / 3);
		const breathingQuality = clamp01((network.breathingSystem as any)?.masteryLevel || 0);
		return {
			nodeCount: clamp01(nodeCount / 1000),
			edgeCount: clamp01(edgeCount / 2000),
			energyLevel,
			linguisticComplexity,
			voicePitch,
			averageNodeEnergy: avgNodeEnergy,
			stability,
			waveIntensity,
			fractalDimension,
			breathingQuality
		};
	}
}


